import { Box, LinearProgress, Theme, Typography } from "@mui/material";

import { VACATION_BID_TEST_ID } from "../testIds";
import { IAwardingSummary } from "./awardingHelpers";

const plural = (count: number, noun: string): string => `${count} ${noun}${count === 1 ? "" : "s"}`;

export interface IAwardingSummaryProps {
  summary: IAwardingSummary;
}

const AwardingSummaryStyles = (theme: Theme): any => ({
  display: "flex",
  alignItems: "center",
  gap: "20px",
  padding: "10px 14px",
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.custom.forms.inputBorder}`,
  borderRadius: "3px",
  "& .summary_label": { fontSize: 11, textTransform: "uppercase", color: theme.custom.forms.labels },
  "& .summary_figure": {
    fontSize: 20,
    lineHeight: 1.2,
    "& .summary_of": { fontSize: 12, color: theme.custom.forms.labels },
  },
  "& .summary_bar": { flex: 1, minWidth: 0 },
  "& .summary_bar .MuiLinearProgress-root": { height: "6px", borderRadius: "3px" },
  "& .summary_bar .MuiLinearProgress-bar": { backgroundColor: theme.palette.success.main },
  "& .summary_detail": { fontSize: 11, color: theme.custom.forms.labels, marginTop: "4px" },
  "& .summary_crew": { textAlign: "right" },
});

/** The round at a glance: how much capacity has been awarded, and how many crew are done. */
export const AwardingSummary = (props: IAwardingSummaryProps): JSX.Element => {
  const { summary } = props;
  const progress = summary.capacity ? Math.min(100, (summary.awarded / summary.capacity) * 100) : 0;

  return (
    <Box sx={AwardingSummaryStyles} data-testid={VACATION_BID_TEST_ID.awardingSummary}>
      <div>
        <Typography className="summary_label">Slots awarded</Typography>
        <Typography className="summary_figure">
          {summary.awarded} <span className="summary_of">{`of ${summary.capacity}`}</span>
        </Typography>
      </div>
      <div className="summary_bar">
        <LinearProgress variant="determinate" value={progress} />
        <Typography className="summary_detail">
          {`${plural(summary.remaining, "slot")} remaining · ${plural(summary.full, "block")} full · ${summary.nearlyFull} nearly full`}
        </Typography>
      </div>
      <div className="summary_crew">
        <Typography className="summary_label">Crew processed</Typography>
        <Typography className="summary_figure">
          {summary.crewComplete} <span className="summary_of">{`of ${summary.crewTotal}`}</span>
        </Typography>
      </div>
    </Box>
  );
};

export default AwardingSummary;
